import { useEffect, useId, useRef, useState } from "react";
import { FieldError, FieldLabel } from "../AdminForm.jsx";
import { buildApprovalInput, buildInvoiceInput, buildReasonInput, formatTotal, utcToday } from "../../lib/productOrders.js";

/**
 * One lifecycle action on one order, as a dialog: record the client's
 * approval and the agreed total, record the QuickBooks invoice, return for a
 * revised quote, correct an invoice recording, or cancel.
 *
 * The dialog builds the request through lib/productOrders.js, shows that
 * module's field errors, and hands a valid body to `onSubmit`. It never sends
 * anything itself, so the caller decides what a success, a conflict or an
 * unknown outcome means. Server-side field errors come back through
 * `serverErrors`; a message that is not about a field through `error`.
 *
 * Focus moves into the dialog when it opens and returns to the opener when
 * it closes; Escape closes it unless a request is in flight.
 */
const COPY = {
  "prepare-billing": { title: "Record approval & prepare billing", submit: "Record approval" },
  invoiced: { title: "Record QuickBooks invoice", submit: "Mark invoiced" },
  revise: { title: "Return for revised quote", submit: "Return for revised quote" },
  "correct-invoice": { title: "Correct invoice recording", submit: "Correct recording" },
  cancel: { title: "Cancel order", submit: "Cancel order" },
};

export function ProductOrderActionModal({ action, order, onSubmit, onClose, pending = false, error = null, serverErrors = {} }) {
  const [form, setForm] = useState({ approvedTotal: "", approvedByName: "", approvalDate: "", approvalReference: "", qbInvoiceNumber: "", invoiceDate: "", reason: "" });
  const [errors, setErrors] = useState({});
  const ids = useId();
  const first = useRef(null);
  const opener = useRef(typeof document !== "undefined" ? document.activeElement : null);
  const today = utcToday();
  const copy = COPY[action];

  useEffect(() => {
    first.current?.focus();
    const returnTo = opener.current;
    return () => { if (returnTo && typeof returnTo.focus === "function") returnTo.focus(); };
  }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !pending) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, pending]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const shown = { ...serverErrors, ...errors };
  const input = "w-full h-9 px-3 rounded border border-border-default text-[13px] text-text-primary bg-surface focus:outline-none focus:border-accent transition-colors disabled:opacity-60";

  function submit(e) {
    e.preventDefault();
    if (pending) return;
    const built = action === "prepare-billing" ? buildApprovalInput(form, order.version, today)
      : action === "invoiced" ? buildInvoiceInput(form, order.version, today)
      : buildReasonInput(form, order.version);
    setErrors(built.errors);
    if (built.input) onSubmit(built.input);
  }

  const field = (name, label, props = {}, help) => (
    <div>
      <FieldLabel><span id={`${ids}-${name}-label`}>{label}</span><span className="text-negative ml-0.5">*</span></FieldLabel>
      <input id={`${ids}-${name}`} aria-labelledby={`${ids}-${name}-label`} ref={props.first ? first : undefined} value={form[name]} onChange={set(name)} disabled={pending} required
        aria-invalid={shown[name] ? true : undefined} aria-describedby={shown[name] ? `${ids}-${name}-error` : help ? `${ids}-${name}-help` : undefined}
        className={input} type={props.type ?? "text"} inputMode={props.inputMode} max={props.max} placeholder={props.placeholder} />
      {shown[name]
        ? <FieldError><span id={`${ids}-${name}-error`}>{shown[name]}</span></FieldError>
        : help && <div id={`${ids}-${name}-help`} className="text-[10px] text-text-muted mt-1">{help}</div>}
    </div>
  );
  const reasonField = (label, help) => (
    <div>
      <FieldLabel><span id={`${ids}-reason-label`}>{label}</span><span className="text-negative ml-0.5">*</span></FieldLabel>
      <textarea id={`${ids}-reason`} aria-labelledby={`${ids}-reason-label`} ref={first} value={form.reason} onChange={set("reason")} disabled={pending} required rows={3} maxLength={1000}
        aria-invalid={shown.reason ? true : undefined} aria-describedby={shown.reason ? `${ids}-reason-error` : `${ids}-reason-help`} className={`${input} h-auto py-2 leading-snug`} />
      {shown.reason ? <FieldError><span id={`${ids}-reason-error`}>{shown.reason}</span></FieldError> : <div id={`${ids}-reason-help`} className="text-[10px] text-text-muted mt-1">{help}</div>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={() => { if (!pending) onClose(); }} />
      <form role="dialog" aria-modal="true" aria-labelledby={`${ids}-title`} aria-describedby={`${ids}-subtitle`} noValidate onSubmit={submit}
        className="relative w-[460px] max-w-full bg-surface rounded-card shadow-xl p-5" aria-busy={pending || undefined}>
        <h2 id={`${ids}-title`} className="text-[15px] font-semibold text-text-primary mb-1">{copy.title}</h2>
        <p id={`${ids}-subtitle`} className="text-[12px] text-text-muted mb-3">
          {order.client?.name} · {order.description.split("\n")[0]} · Qty {order.quantity}{order.approvedTotal !== null ? ` · ${formatTotal(order.approvedTotal)}` : ""}
        </p>

        <div className="space-y-2.5">
          {action === "prepare-billing" && (
            <>
              <p className="text-[12px] text-text-secondary">Approval recorded from the client. Enter the total the client approved for this entire order — the quote basis from QuickBooks, not a unit rate; the quantity is not multiplied in. Prism does not add tax, markup or setup fees. If the final QuickBooks quote changes the total or scope, get approval of the revised quote before billing.</p>
              {field("approvedTotal", "Approved total for this entire order (USD)", { first: true, inputMode: "decimal", placeholder: "1250.00" }, "A decimal amount above zero, at most two decimals.")}
              {field("approvedByName", "Approving client’s name", { placeholder: "Who at the client approved it" })}
              {field("approvalDate", "Approval date", { type: "date", max: today })}
              {field("approvalReference", "Approval reference", { placeholder: "Email subject and date, call note…" }, "Where the approval is recorded, so it can be found again.")}
            </>
          )}
          {action === "invoiced" && (
            <>
              <p className="text-[12px] text-text-secondary">Invoice already created in QuickBooks. Enter its number and date; Prism records the reference and does not create or change anything in QuickBooks. One QuickBooks invoice may carry several orders.</p>
              {field("qbInvoiceNumber", "QuickBooks invoice number", { first: true })}
              {field("invoiceDate", "Invoice date", { type: "date", max: today })}
            </>
          )}
          {action === "revise" && (
            <>
              <p className="text-[12px] text-text-secondary">The order goes back to <strong>Awaiting quote</strong>. Its current approval and agreed total are cleared and kept in the history; the details can then be edited and a revised quote approved.</p>
              {reasonField("Reason", "Why the quote must be revised — for example, the client changed the quantity or stock.")}
            </>
          )}
          {action === "correct-invoice" && (
            <>
              <p className="text-[12px] text-text-secondary">This changes Prism only. The order returns to <strong>Ready for billing</strong> with its invoice number and date cleared and kept in the history; the approved total is unchanged. It does not cancel or modify the invoice in QuickBooks — a refund, credit or cancellation must be handled there first.</p>
              {reasonField("Reason", "What was recorded wrongly.")}
            </>
          )}
          {action === "cancel" && (
            <>
              <p className="text-[12px] text-text-secondary">A cancelled order stays in the history and cannot be reopened; a new request would be a new order. {order.approvedTotal !== null ? "Its approved total is kept for the record." : "No charge was ever agreed."}</p>
              {reasonField("Reason", "Why the order is cancelled.")}
            </>
          )}
        </div>

        {error && <p role="alert" className="text-[12px] text-negative mt-3">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" disabled={pending} onClick={onClose} className="px-3 py-1.5 text-[13px] text-text-secondary disabled:opacity-50">Close</button>
          <button type="submit" disabled={pending} className="px-3 py-1.5 rounded-card bg-accent text-white text-[13px] font-medium disabled:opacity-50">{pending ? "Saving…" : copy.submit}</button>
        </div>
      </form>
    </div>
  );
}
